import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/layout/DashboardLayout';
import ErrorBoundary from './components/ErrorBoundary';
import api from './utils/api';

// Lazy-loaded page components
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const CasesPage = React.lazy(() => import('./pages/CasesPage'));
const CaseDetailPage = React.lazy(() => import('./pages/CaseDetailPage'));
const AnalyticsPage = React.lazy(() => import('./pages/AnalyticsPage'));
const AIFeedbackPage = React.lazy(() => import('./pages/AIFeedbackPage'));
const DamageIntakePage = React.lazy(() => import('./pages/DamageIntakePage'));
const BulkUploadPage = React.lazy(() => import('./pages/BulkUploadPage'));
const VehicleDebugPage = React.lazy(() => import('./pages/VehicleDebugPage'));
const UserManagementPage = React.lazy(() => import('./pages/UserManagementPage'));
const ConfigurationPage = React.lazy(() => import('./pages/ConfigurationPage'));
const ApiDocumentationPage = React.lazy(() => import('./pages/ApiDocumentationPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const PotentialDuplicatesPage = React.lazy(() => import('./pages/PotentialDuplicatesPage'));
const BillingPage = React.lazy(() => import('./pages/BillingPage'));
const ChargeDetailPage = React.lazy(() => import('./pages/ChargeDetailPage'));
const MemberDetailPage = React.lazy(() => import('./pages/MemberDetailPage'));
const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    // Check auth via httpOnly cookie (jwt_token sent automatically by browser)
    const checkAuth = async () => {
      try {
        const response = await api.get('/auth/me');
        setIsAuthenticated(true);
        setUserRole(response.data.role);
      } catch {
        setIsAuthenticated(false);
        setUserRole(null);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore logout API errors; proceed with local state reset
    }
    setIsAuthenticated(false);
    setUserRole(null);
    navigate('/login');
    toast.success('Logged out successfully');
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-slate-600 dark:text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            isAuthenticated ? (
              <DashboardLayout onLogout={handleLogout}>
                <ErrorBoundary>
                <React.Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="text-slate-600 dark:text-slate-400">Loading...</div></div>}>
                  <Routes>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/cases" element={<CasesPage />} />
                    <Route path="/case/:caseId" element={<CaseDetailPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/ai-feedback" element={<AdminRoute userRole={userRole}><AIFeedbackPage /></AdminRoute>} />
                    <Route path="/potential-duplicates" element={<OpsRoute userRole={userRole}><PotentialDuplicatesPage /></OpsRoute>} />
                    <Route path="/intake" element={<DamageIntakePage />} />
                    <Route path="/bulk-upload" element={<BulkUploadPage />} />
                    <Route path="/vehicle-debug" element={<VehicleDebugPage />} />
                    <Route path="/users" element={<AdminRoute userRole={userRole}><UserManagementPage /></AdminRoute>} />
                    <Route path="/config" element={<AdminRoute userRole={userRole}><ConfigurationPage /></AdminRoute>} />
                    <Route path="/api-docs" element={<AdminRoute userRole={userRole}><ApiDocumentationPage /></AdminRoute>} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/billing" element={<BillingPage />} />
                    <Route path="/billing/charges/:chargeId" element={<ChargeDetailPage />} />
                    <Route path="/billing/members/:memberId" element={<MemberDetailPage />} />
                    <Route path="/billing/payment-success" element={<PaymentSuccessPage />} />
                    <Route path="/" element={<Navigate to="/dashboard" />} />
                  </Routes>
                </React.Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </>
  );
}

function App() {
  // Initialize theme on app load
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <Router>
          <AppContent />
        </Router>
      </div>
    </ErrorBoundary>
  );
}

// Ops staff or admin route protection
function OpsRoute({ children, userRole }) {
  if (!['ops_staff', 'admin'].includes(userRole)) {
    toast.error('Access denied. Operations staff or admin privileges required.');
    return <Navigate to="/dashboard" />;
  }
  return children;
}

// Admin-only route protection
function AdminRoute({ children, userRole }) {
  if (userRole !== 'admin') {
    toast.error('Access denied. Admin privileges required.');
    return <Navigate to="/dashboard" />;
  }
  return children;
}

export default App;
export { api };